if (!customElements.get('carousel-slider')) {
	customElements.define('carousel-slider', class CarouselSlider extends HTMLElement {
		constructor() {
			super();
			this._initialized = false;
		}

		connectedCallback() {
			this.carousel = this.querySelector('.grid-carousel');
			if (this.carousel) {
				// For recently-viewed, the carousel might be empty initially
				// Wait for content to be added before initializing
				if (this.carousel.children.length === 0) {
					this.waitForContent();
				} else {
					this.initialize();
				}
			}
		}

		waitForContent() {
			// Use MutationObserver to wait for grid items to be added to the carousel
			const observer = new MutationObserver((mutations) => {
				for (const mutation of mutations) {
					if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
						// Check if grid items were added
						const hasGridItems = [...mutation.addedNodes].some(node => 
							node.nodeType === Node.ELEMENT_NODE && 
							(node.classList?.contains('grid-item') || node.querySelector?.('.grid-item'))
						);
						
						if (hasGridItems && !this._initialized) {
							observer.disconnect();
							// Small delay to ensure all items are added
							setTimeout(() => this.initialize(), 50);
							break;
						}
					}
				}
			});
			
			observer.observe(this.carousel, { 
				childList: true, 
				subtree: true 
			});
			
			// Fallback: also try after a delay
			setTimeout(() => {
				if (!this._initialized && this.carousel.children.length > 0) {
					observer.disconnect();
					this.initialize();
				}
			}, 500);
		}

		initialize() {
			if (this._initialized || !this.carousel) return;
			
			// Flickity doesn't work with flex containers, so
			// to avoid flash of unstyled content and page jump, style the container
			// as block just before initialization. On mobile where the carousel
			// is converted to simple scroller we swap this to display: flex !important
			// using CSS.
			this.carousel.style.display = 'block';
			
			// Get settings from data attributes
			const enableLoop = this.getAttribute('data-loop') === 'true';
			const centerSlides = this.getAttribute('data-center') === 'true';
			
			try {
				this.flickity = new Flickity(this.carousel, {
					cellAlign: centerSlides ? 'center' : 'left',
					groupCells: false,
					contain: true,
					resize: true,
					draggable: true,
					prevNextButtons: false,
					fade: false,
					cellSelector: '.grid-item',
					initialIndex: 0,
					pageDots: false,
					wrapAround: enableLoop,
					accessibility: false,
					watchCSS: true,
				});
				
				this.flickity.on('change', (index) => {
					const event = new CustomEvent('slider:slide-change', {
						detail: {
							id: this.getAttribute('id'),
							index,
						},
					});
					document.dispatchEvent(event);
				});
				
				this._initialized = true;
			} catch (error) {
				console.warn('Carousel slider initialization failed:', error);
			}
		}
	});
}

if (!customElements.get('carousel-navigation')) {
	customElements.define('carousel-navigation', class CarouselNavigation extends HTMLElement {
		constructor() {
			super();
		}

		connectedCallback() {
			this.init();
		}

		init(retry = 1, maxRetry = 3) {
			if (retry === maxRetry) {
				return;
			}

			const id = this.getAttribute('for');
			this.carouselComponent = document.querySelector(`#${id}`);

			if ((!this.carouselComponent || !this.carouselComponent.flickity)) {
				setTimeout(() => {
					this.init(retry + 1);
				}, 350);
				return;
			}

			this.flickity = this.carouselComponent.flickity;
			this.buttonPrev = this.querySelector('.js-prev');
			this.buttonNext = this.querySelector('.js-next');

			this.buttonPrev.addEventListener('click', (event) => {
				event.preventDefault();
				this.flickity.previous();
			});

			this.buttonNext.addEventListener('click', (event) => {
				event.preventDefault();
				this.flickity.next();
			});

			this.flickity.on('select', () => {
				this.enableButton(this.buttonPrev);
				this.enableButton(this.buttonNext);

				if (!this.flickity.slides[this.flickity.selectedIndex - 1]) {
					this.disableButton(this.buttonPrev);
				} else if (!this.flickity.slides[this.flickity.selectedIndex + 1]) {
					this.disableButton(this.buttonNext);
				}
			});
		}

		enableButton(button) {
			button.classList.remove('pill-nav-item-disabled');
			button.classList.remove('focus-none');
			button.setAttribute('aria-disabled', 'false');
		}

		disableButton(button) {
			button.classList.add('pill-nav-item-disabled');
			button.classList.add('focus-none');
			button.setAttribute('aria-disabled', 'true');
		}
	});
}
